export default {
    meta: {
        title: "RetterPlus: Emergency App"
    },
    header: {
        appName: "RetterPlus",
        download: "Download",
        selectLanguage: "Select Language",
        share: "Share"
    },
    hero: {
        headline: "Automatic Safety for Everyone. Instant Peace of Mind.",
        subheading: "Help is just three knocks away. With RetterPlus, a triple knock on your phone instantly alerts your loved ones, with no unlocking and no searching for buttons. An experimental fall detection feature adds automatic protection, configurable for low, medium or high intensity falls. Ideal for seniors, people with health conditions, hikers, cyclists and travellers alike.",
        getItOnGooglePlay: "Get it on Google Play",
        phoneMockupAlt: "RetterPlus app mockup on a phone",
        shareMessage: "Hey! Check out this emergency app I found called RetterPlus. It turns a smartphone into a complete safety net for you and your loved ones, whether at home, on a hike, or travelling.\n\nHere is what it does:\n• Auto fall detection\n• Direct SOS alerts (Phone calls & SMS)\n• Triple knock alerts (Super easy way to call for help)\n• Inactivity alerts (To catch silent emergencies)\n\nYou can check it out here:\nhttps://play.google.com/store/apps/details?id=com.retterplus.android"
    },
    problem: {
        headline: "Life is Unpredictable. Your Safety Shouldn't Be.",
        body: "A sudden fall at home. A serious accident on a mountain trail, a bike ride or your daily commute. A medical emergency in an unfamiliar city. These are moments where every second counts, and reaching for your phone might be impossible. RetterPlus was built for these moments."
    },
    detailedFeatures: {
        callTitle: "Direct SOS Phone Call",
        callDescription: "Whenever an emergency is confirmed (fall, inactivity, triple knock, or manual SOS), RetterPlus initiates a direct phone call to your emergency contacts. This critical layer ensures your alert is heard immediately, even if they miss the SMS. No app needed for contacts—just a phone.",
        knockTitle: "Triple Knock Alert",
        knockDescription: "In distress but can't reach your screen? Simply knock your phone 3+ times (on your arm, leg, or any surface) to trigger an SOS alert. This discreet and rapid activation ensures help is on the way without fumbling for buttons.",
        fallTitle: "Advanced Fall Detection with Sensitivity Settings",
        fallDescription: "Tailor fall detection to your specific needs. Choose 'High' for maximum protection if you're prone to falls, or 'Standard' / 'Low' to minimize false alarms from everyday bumps. You stay in control of your safety.",
        fallFootnote: "* Fall detection is experimental. Depending on the sensitivity you choose, it may miss some falls or trigger false alarms on regular bumps or jerks.",
        inactivityTitle: "Smart Inactivity Monitoring",
        inactivityDescription: "Silent emergencies can happen. RetterPlus notices when your phone hasn't been moved for a long time and checks in with you. If you don't respond to the countdown, it automatically alerts your contacts, ensuring you're never left without help.",
        medicalIdTitle: "Medical ID — Critical Info When It Matters Most",
        medicalIdDescription: "If you're unconscious, first responders have seconds to make life-or-death decisions. Medical ID gives them what they need — instantly. Accessible directly from the Triple Knock screen, above the lock screen, no unlock required. Your blood type, allergies, and active medications are right there. So are your emergency contacts, reachable with a single tap. And that's just the start — Medical ID holds everything a doctor or paramedic could need to give you the right care, right away."
    },
    features: {
        headline: "More Safety Features",
        impactTitle: "Impact Detection",
        impactDescription: "Senses the force of a major impact, like a car crash, and automatically starts the alert process for commuters and drivers.",
        batteryTitle: "Low Battery Alerts",
        batteryDescription: "Reminds you to charge your phone, ensuring your protection is always powered and ready.",
        falseAlarmTitle: "False Alarm Prevention",
        falseAlarmDescription: "Countdown screens give you time to cancel alerts for accidental bumps or movements, preventing unnecessary panic.",
        customizableTitle: "Highly Customizable",
        customizableDescription: "Enable or disable features as you need them. Adjust sensitivity settings to match your lifestyle and risk level."
    },
    keyDifference: {
        smsHeadline: "Help That Always Gets Through.",
        smsBody: "In an emergency, RetterPlus sends a standard SMS with your GPS location & a phone call to your chosen contacts. They don't need the app, a smartphone, or an internet connection. Simple and reliable.",
        privacyHeadline: "Your Privacy is Our Promise.",
        privacyBody: "We believe your data is yours. Your location is kept private on your device and is only shared with your emergency contacts during an active alert. No tracking, no data selling. Ever."
    },
    whoIsItFor: {
        headline: "Who is RetterPlus for?",
        seniorsTitle: "Seniors Living Independently:",
        seniorsDescription: "Providing confidence and a safety net for aging loved ones.",
        healthTitle: "People with Health Conditions:",
        healthDescription: "Fast help and critical medical info if something goes wrong, from epilepsy and heart conditions to diabetes.",
        outdoorTitle: "Hikers, Cyclists & Outdoor Enthusiasts:",
        outdoorDescription: "Help can reach you even on remote trails, where a fall or crash could go unnoticed.",
        travellersTitle: "Frequent Travellers:",
        travellersDescription: "Peace of mind in unfamiliar cities and countries, far from home.",
        unsafeTitle: "Travel in Unsafe Areas:",
        unsafeDescription: "A discreet triple-knock SOS when pulling out your phone isn't an option.",
        commutersTitle: "Daily Commuters:",
        commutersDescription: "Adding an extra layer of automated protection on the road.",
        familiesTitle: "Families & Loved Ones:",
        familiesDescription: "The reassurance of knowing you'll be notified in an emergency.",
        loneWorkersTitle: "Lone Workers & Solo Livers:",
        loneWorkersDescription: "A silent partner for anyone who works or lives alone."
    },
    cta: {
        headline: "Safety for You. Reassurance for Those Who Love You.",
        subheading: "Download RetterPlus today and carry peace of mind in your pocket."
    },
    footer: {
        headline: "Meet the Team",
        body: "\"We built RetterPlus with a simple mission: to use technology to help keep our loved ones safe. We hope this app brings you and your family a little more peace of mind.\"",
        author: "— Your RetterPlus Team",
        privacyPolicy: "Privacy Policy",
        contact: "Contact",
        copyright: "{year} RetterPlus. All rights reserved.",
        joergName: "Jörg Haupt",
        joergRole: "Business Co-Founder",
        joergBio: "With over two decades of experience in the German market, Jörg leads our business operations and partnerships in Germany, ensuring RetterPlus reaches the people who need it most.",
        prasadName: "Prasad Pawar",
        prasadRole: "Technical Co-Founder",
        prasadBio: "With over a decade in software engineering and a focus on mobile apps, Prasad built RetterPlus from the ground up — obsessing over every detail so the app works flawlessly when it matters most."
    },
    gdpr: {
        headline: "100% DSGVO (GDPR) Compliant",
        body: "We take your data privacy seriously. RetterPlus is fully compliant with the General Data Protection Regulation (DSGVO). Your data is processed securely and is never shared with third parties without your explicit consent."
    },
    affiliate: {
        welcomeTitle: "Welcome to RetterPlus",
        installInfo: "RetterPlus can only be installed via the official Google Play Store.",
        chooserInstruction: "If your phone asks, please select Google Play Store.",
        fallbackText: "Open Google Play Store"
    },
    goldMembership: {
        eyebrow: "RetterPlus Gold",
        headline: "Full Protection. One Simple Plan.",
        subheadline: "Get the most out of RetterPlus with Gold — powerful safety features for less than a coffee a month.",
        planName: "RetterPlus Gold",
        planTagline: "Complete safety, every day.",
        perYear: "per year",
        perMonth: "just €5 / month",
        goldFeaturesTitle: "Gold Features",
        freeFeaturesTitle: "Always Free",
        footerText: "Cancel anytime. No hidden fees. Billed annually through Google Play.",
        paidFeatures: [
            { title: "Triple Knock Alert", desc: "Knock your phone 3 times to silently call for help" },
            { title: "Automatic Fall Detection", desc: "Experimental sensor-based fall sensing" },
            { title: "Inactivity Monitoring", desc: "Checks in and alerts your contacts if your phone isn\'t moved for too long" },
            { title: "Mobility Reminders", desc: "Science-backed nudges to keep you moving" }
        ],
        freeFeatures: [
            { title: "Safety Check-in", desc: "Confirm you\'re safe after a trip or journey" },
            { title: "Low Battery Alerts", desc: "Notifies you and your contacts when battery is low" },
            { title: "Floating SOS Button", desc: "Always-visible emergency button across all apps" },
            { title: "Weekly SMS Report", desc: "A summary sent to your emergency contacts" }
        ]
    }
};
